import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiBell, FiLogOut, FiRefreshCw, FiSend } from 'react-icons/fi';
import Login from './Login';
import ApiService from '../services/ApiService';
import SignalRService from '../services/SignalRService';
import '../styles/MobileApp.css';

const MOBILE_MESSAGE_LIMIT = 60;
const MOBILE_FRONTEND_VERSION = (process.env.REACT_APP_FRONT_VERSION || '1.4.8').trim();

function MobileApp() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() => getNotificationPermission());
  const chatsRef = useRef([]);
  const selectedChatRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const loadChats = useCallback(async () => {
    setChatLoading(true);
    setError('');
    try {
      const data = await ApiService.getChats();
      setChats(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить чаты');
    } finally {
      setChatLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    setMessagesLoading(true);
    setError('');
    try {
      const data = await ApiService.getMessages(chatId, MOBILE_MESSAGE_LIMIT, 0);
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить сообщения');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const requestNotifications = async () => {
    if (!supportsNotifications()) {
      setError('Браузер не поддерживает системные уведомления');
      return;
    }

    try {
      const permission = await window.Notification.requestPermission();
      setNotificationPermission(permission);
      setError(permission === 'granted' ? '' : 'Разрешение на уведомления не выдано');
    } catch {
      setError('Не удалось включить уведомления');
    }
  };

  const showSystemNotification = useCallback((chatId, title, body) => {
    if (!supportsNotifications() || window.Notification.permission !== 'granted') {
      return;
    }

    try {
      const notification = new window.Notification(title || 'Новое сообщение', {
        body: body || 'Откройте диалог для просмотра',
        tag: `max-chat-${chatId}`,
        renotify: true,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        const chat = chatsRef.current.find((item) => Number(item.chatId) === Number(chatId));
        setSelectedChat(chat || { chatId, firstName: title || 'Диалог' });
        loadMessages(chatId).catch(() => {});
        notification.close();
      };
    } catch {
      // Mobile browsers and embedded containers can block notifications.
    }
  }, [loadMessages]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('user_token');
        const rawUser = localStorage.getItem('user');
        if (!token || !rawUser) {
          setLoadingAuth(false);
          return;
        }

        const parsedUser = JSON.parse(rawUser);
        const authData = await ApiService.checkAuth();
        const freshUser = authData?.user || parsedUser;
        setUser(freshUser);
      } catch {
        localStorage.removeItem('user_token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoadingAuth(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let mounted = true;
    const setup = async () => {
      await loadChats();
      try {
        await SignalRService.connect();
        await SignalRService.subscribeToAllChats();
        const connection = SignalRService.getConnection();
        if (!connection || !mounted) return;

        const onChatUpdated = async () => {
          await loadChats();
        };

        const onNewMessage = (data) => {
          const chatId = Number(data?.chatId);
          if (!chatId) return;
          const nextMessage = data?.message;
          const currentChatId = Number(selectedChatRef.current?.chatId);
          const isIncoming = nextMessage?.isFromBot !== false;
          const shouldNotify = isIncoming && (document.hidden || currentChatId !== chatId);
          if (shouldNotify) {
            const chat = chatsRef.current.find((item) => Number(item.chatId) === chatId);
            showSystemNotification(chatId, chat ? getChatName(chat) : 'Новое сообщение', getMessageText(nextMessage));
          }
          if (Number(selectedChatRef.current?.chatId) === chatId) {
            setMessages((prev) => {
              if (!nextMessage) return prev;
              if (prev.some((m) => getMessageKey(m) === getMessageKey(nextMessage))) {
                return prev;
              }
              return [...prev, nextMessage];
            });
          }
          loadChats();
        };

        connection.on('ChatUpdated', onChatUpdated);
        connection.on('NewMessage', onNewMessage);

        return () => {
          connection.off('ChatUpdated', onChatUpdated);
          connection.off('NewMessage', onNewMessage);
        };
      } catch (err) {
        setError(err.message || 'SignalR недоступен');
      }
    };

    let cleanup;
    setup().then((fn) => {
      cleanup = fn;
    });

    return () => {
      mounted = false;
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [loadChats, showSystemNotification, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, selectedChat?.chatId]);

  const sortedChats = useMemo(() => {
    return [...chats].sort((a, b) => {
      const at = parseDateMs(a.lastMessageTime ?? a.last_message_time);
      const bt = parseDateMs(b.lastMessageTime ?? b.last_message_time);
      return bt - at;
    });
  }, [chats]);

  const handleLoginSuccess = (nextUser) => {
    setUser(nextUser);
  };

  const handleLogout = async () => {
    await ApiService.logout();
    setUser(null);
    setSelectedChat(null);
    setMessages([]);
  };

  const openChat = async (chat) => {
    setSelectedChat(chat);
    setDraft('');
    await SignalRService.subscribeToChat(chat.chatId);
    await loadMessages(chat.chatId);
    try {
      await ApiService.markChatAsRead(chat.chatId);
      await loadChats();
    } catch {
      // read state should not block opening the dialog
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!selectedChat || !text || sending) return;

    setSending(true);
    setError('');
    try {
      const authorName = `${user?.displayName || user?.name || ''}`.trim();
      const signedText = authorName
        ? `<b>${escapeHtml(authorName)}:</b><br><br>${escapeHtml(text)}`
        : text;
      const saved = await ApiService.sendMessage({
        chatId: selectedChat.chatId,
        text: signedText,
        format: authorName ? 'html' : null,
        isFromBot: false
      });
      setMessages((prev) => (
        prev.some((m) => getMessageKey(m) === getMessageKey(saved))
          ? prev
          : [...prev, saved]
      ));
      setDraft('');
      await loadChats();
    } catch (err) {
      setError(err.message || 'Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  if (loadingAuth) {
    return <div className="mobile-app mobile-center">Загрузка...</div>;
  }

  if (!user) {
    return (
      <div className="mobile-auth">
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  return (
    <div className="mobile-app">
      {!selectedChat ? (
        <section className="mobile-chat-list-screen">
          <header className="mobile-topbar">
            <div>
              <div className="mobile-brand">KAT-Telecom</div>
              <div className="mobile-subtitle">MAX Messenger v{MOBILE_FRONTEND_VERSION}</div>
            </div>
            <div className="mobile-topbar-actions">
              {supportsNotifications() && notificationPermission !== 'granted' && (
                <button type="button" className="mobile-icon-btn" onClick={requestNotifications} title="Включить уведомления">
                  <FiBell />
                </button>
              )}
              <button type="button" className="mobile-icon-btn" onClick={loadChats} title="Обновить">
                <FiRefreshCw />
              </button>
              <button type="button" className="mobile-icon-btn" onClick={handleLogout} title="Выйти">
                <FiLogOut />
              </button>
            </div>
          </header>

          {error && <div className="mobile-error">{error}</div>}
          {chatLoading && <div className="mobile-loading">Обновляем чаты...</div>}

          <div className="mobile-chat-list">
            {sortedChats.length === 0 && !chatLoading ? (
              <div className="mobile-empty">Чатов пока нет</div>
            ) : (
              sortedChats.map((chat) => (
                <button
                  type="button"
                  key={chat.chatId || chat.id}
                  className="mobile-chat-item"
                  onClick={() => openChat(chat)}
                >
                  <div className="mobile-avatar">
                    {chat.icon?.url ? (
                      <img src={chat.icon.url} alt="" />
                    ) : (
                      getInitials(chat)
                    )}
                  </div>
                  <div className="mobile-chat-body">
                    <div className="mobile-chat-row">
                      <span className="mobile-chat-name">
                        {getChatName(chat)}
                        {chat.isMiniAppAuthorized && <span className="mobile-star">★</span>}
                      </span>
                      <span className="mobile-chat-time">{formatShortTime(chat.lastMessageTime ?? chat.last_message_time)}</span>
                    </div>
                    <div className="mobile-chat-preview">{getPreviewText(chat.lastMessage)}</div>
                  </div>
                  {Number(chat.unreadCount || 0) > 0 && (
                    <span className="mobile-unread">{Number(chat.unreadCount) > 99 ? '99+' : chat.unreadCount}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </section>
      ) : (
        <section className="mobile-dialog-screen">
          <header className="mobile-dialog-header">
            <div className="mobile-dialog-peer">
              <div className="mobile-dialog-avatar">
                {selectedChat.icon?.url ? (
                  <img src={selectedChat.icon.url} alt="" />
                ) : (
                  getInitials(selectedChat)
                )}
              </div>
              <div className="mobile-dialog-title">
                <strong>{getChatName(selectedChat)}</strong>
                <span>{formatShortTime(selectedChat.lastMessageTime ?? selectedChat.last_message_time)}</span>
              </div>
            </div>
          </header>

          {error && <div className="mobile-error">{error}</div>}

          <div className="mobile-messages">
            {messagesLoading ? (
              <div className="mobile-loading">Загрузка сообщений...</div>
            ) : (
              messages.map((message) => {
                const outgoing = !message.isFromBot;
                return (
                  <div
                    key={message.messageId || message.id || `${message.timestamp}-${message.text}`}
                    className={`mobile-message ${outgoing ? 'outgoing' : 'incoming'}`}
                  >
                    <div className="mobile-message-bubble">
                      <div className="mobile-message-text">{getMessageText(message)}</div>
                      {message.attachments && <div className="mobile-attachment">📎 Вложение</div>}
                      <div className="mobile-message-time">{formatMessageTime(message.timestamp)}</div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="mobile-composer" onSubmit={sendMessage}>
            <button type="button" className="mobile-back-btn" onClick={() => setSelectedChat(null)}>
              <FiArrowLeft />
            </button>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Сообщение..."
              rows={1}
            />
            <button type="submit" disabled={sending || !draft.trim()}>
              <FiSend />
            </button>
          </form>
        </section>
      )}
    </div>
  );
}

function getChatName(chat) {
  return `${chat?.firstName || ''} ${chat?.lastName || ''}`.trim() || `Чат ${chat?.chatId || ''}`;
}

function getInitials(chat) {
  const name = getChatName(chat);
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase() || 'U';
}

function getPreviewText(value) {
  const text = stripHtml(value || '').trim();
  if (!text || text === '[Пустое сообщение]') return 'Вложение';
  return text.length > 90 ? `${text.slice(0, 90)}...` : text;
}

function getMessageText(message) {
  const text = stripHtml(message?.text || message?.message || '').trim();
  return text || '[Пустое сообщение]';
}

function getMessageKey(message) {
  if (!message) return '';
  const id = message.messageId || message.id || message.mid;
  if (id) return `id:${id}`;
  return `fallback:${message.chatId || ''}:${message.timestamp || ''}:${message.text || message.message || ''}`;
}

function supportsNotifications() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function getNotificationPermission() {
  return supportsNotifications() ? window.Notification.permission : 'unsupported';
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>');
}

function stripHtml(value) {
  if (!value) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${value}</div>`, 'text/html');
  return (doc.body.textContent || '').replace(/\u00A0/g, ' ');
}

function parseDateMs(value) {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  const raw = String(value).trim();
  if (!raw) return 0;
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(raw) ? `${raw}Z` : raw;
  const ms = new Date(normalized).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

function formatShortTime(value) {
  const ms = parseDateMs(value);
  if (!ms) return '';
  const date = new Date(ms);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function formatMessageTime(value) {
  const ms = parseDateMs(value);
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export default MobileApp;
